import { useEffect } from 'react'
import { useRouter } from 'next/router'

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

// Known search-engine domain roots. Match GA4 convention: an inbound click
// from any of these gets utm_source=<root> and utm_medium=organic, rather
// than the literal hostname + 'referral'.
const SEARCH_ENGINES = new Set([
  'google',
  'bing',
  'yahoo',
  'duckduckgo',
  'ecosia',
  'yandex',
  'baidu',
  'qwant',
  'startpage',
  'brave', // search.brave.com — was falling through to referral→Direct
  'kagi',
])

function matchSearchEngine(hostname: string): string | null {
  for (const label of hostname.split('.')) {
    if (SEARCH_ENGINES.has(label)) return label
  }
  return null
}

// Payment processors / hosted-checkout domains the buyer is redirected TO during
// checkout (e.g. GoCardless direct-debit, Chargebee hosted pages). On return,
// document.referrer is the processor — NOT a real acquisition channel — so we
// exclude them like self-referrals. Without this a sale gets a bogus
// `pay.gocardless.com / referral` source that masks its true origin/Direct.
const PAYMENT_REFERRERS = new Set([
  'gocardless',
  'chargebee',
  'paypal',
  'stripe',
  'adyen',
  'braintree',
])

function isPaymentReferrer(hostname: string): boolean {
  for (const label of hostname.split('.')) {
    if (PAYMENT_REFERRERS.has(label)) return true
  }
  return false
}

export function getUtmParam(param: string): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(param)
}

export function captureUtmParameters() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const urlParams = new URLSearchParams(window.location.search)

    UTM_PARAMS.forEach((param) => {
      const value = urlParams.get(param)
      if (value) {
        localStorage.setItem(param, value)
      }
    })

    // Microsoft Bing Ads click ID — captured the same way as UTMs so it
    // survives a multi-page journey from a Bing ad to checkout. Read back
    // at checkout-start by project/checkout-attribution.ts.
    const msclkid = urlParams.get('msclkid')
    if (msclkid) {
      localStorage.setItem('msclkid', msclkid)
    }

    // Raw document.referrer URL — first-touch. Persisted on landing so we
    // have the source URL for attribution forensics. Forwarded at
    // checkout-start and written to Chargebee customer.cf_referrer_url.
    // Truncated to 99 chars to fit Chargebee's Single Line Text cap —
    // keeps origin + path; loses long query strings on search URLs, which
    // is acceptable for source attribution (the host is what matters).
    if (!localStorage.getItem('referrer') && document.referrer) {
      localStorage.setItem('referrer', document.referrer.slice(0, 99))
    }

    // First landing URL (path + query) — first-touch entry point of the
    // journey. Persisted once on first landing, read at checkout-start and
    // written to Chargebee customer.cf_landing_page (Multiline, 250 cap).
    if (!localStorage.getItem('landing_page')) {
      localStorage.setItem(
        'landing_page',
        `${window.location.pathname}${window.location.search}`.slice(0, 250),
      )
    }

    // If Tolt referral data is present, mark the source as Tolt. We deliberately
    // do NOT write the program_id into utm_campaign: the program_id is already
    // captured on its own (window.tolt_referral → customer.cf_tolt_referral), and
    // utm_campaign persists in localStorage indefinitely — a stale `prg_...` value
    // then leaked onto later organic/referral visits (seen as google/organic rows
    // carrying campaign=prg_... in BigQuery).
    const toltData = (window as any).tolt_data
    if (toltData?.program_id) {
      // Only set if not already set by URL parameters
      if (!localStorage.getItem('utm_source')) {
        localStorage.setItem('utm_source', 'tolt')
      }
      // Label the channel as affiliate so reports read tolt/affiliate instead
      // of tolt/(none). The program_id is deliberately NOT written to
      // utm_campaign (see above) — it persists separately as cf_tolt_referral.
      if (!localStorage.getItem('utm_medium')) {
        localStorage.setItem('utm_medium', 'affiliate')
      }
    }

    // Click-ID → source synthesis. A click ID is a higher-confidence origin
    // signal than document.referrer and survives referrer stripping, so it runs
    // BEFORE the referrer fallback below. Without this, an auto-tagged Google
    // Ads click (gclid present, no manual UTM) falls through to the referrer
    // rule, gets tagged google/organic, and is misreported as Organic Search
    // instead of Paid Search. gclid/gbraid/wbraid (Google) and msclkid (Bing)
    // are issued ONLY on ad clicks → unambiguously paid (utm_medium=cpc).
    // fbclid is NOT paid-only — Facebook appends it to every outbound link,
    // organic posts included — so it maps to Organic Social (medium=social),
    // not Paid Social; genuine paid Meta clicks carry an explicit utm anyway.
    // The content-os classifier maps adwords→Google Ads, bing+cpc→Microsoft
    // Ads, facebook+social→Organic Social.
    if (!localStorage.getItem('utm_source')) {
      const googleClick =
        urlParams.get('gclid') ||
        urlParams.get('gbraid') ||
        urlParams.get('wbraid')
      const bingClick = urlParams.get('msclkid')
      const metaClick = urlParams.get('fbclid')

      const setSource = (source: string, medium: string) => {
        localStorage.setItem('utm_source', source)
        if (!localStorage.getItem('utm_medium')) {
          localStorage.setItem('utm_medium', medium)
        }
      }

      if (googleClick) {
        setSource('adwords', 'cpc')
      } else if (bingClick) {
        setSource('bing', 'cpc')
      } else if (metaClick) {
        setSource('facebook', 'social')
      }
    }

    // Fall back to document.referrer when no real or Tolt utm_source was
    // captured. Search engines (Google, Bing, etc.) get utm_medium=organic
    // and utm_source=<engine root> per GA4 convention; everything else is
    // utm_source=<hostname> + utm_medium=referral. The classifier in
    // content-os maps the rest of the taxonomy downstream.
    if (!localStorage.getItem('utm_source') && document.referrer) {
      try {
        const refHost = new URL(document.referrer).hostname
          .replace(/^www\./, '')
          .toLowerCase()
        if (
          refHost &&
          !refHost.endsWith('hoxtonmix.com') &&
          !isPaymentReferrer(refHost)
        ) {
          const engine = matchSearchEngine(refHost)
          if (engine) {
            localStorage.setItem('utm_source', engine)
            if (!localStorage.getItem('utm_medium')) {
              localStorage.setItem('utm_medium', 'organic')
            }
          } else {
            localStorage.setItem('utm_source', refHost)
            if (!localStorage.getItem('utm_medium')) {
              localStorage.setItem('utm_medium', 'referral')
            }
          }
        }
      } catch {
        // ignore — invalid referrer URL
      }
    }
  } catch (e) {
    console.error('Error capturing UTM parameters:', e)
  }
}

export default function useUtmTracking() {
  const router = useRouter()

  useEffect(() => {
    // Capture UTM parameters on initial page load
    captureUtmParameters()

    // Capture UTM parameters on route changes
    const handleRouteChange = () => {
      captureUtmParameters()
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])
}
